import { ArrowUpRight, Archive, Megaphone, MessageSquare, MailOpen, MailX, MoreHorizontal, ShieldCheck } from 'lucide-react';
import { type ReactElement, useEffect, useRef, useState } from 'react';

import { cn } from '../ui/utils';

export type NotificationKind = 'transaction' | 'system' | 'marketing';

type NotificationCardProps = {
  title: string;
  preview: string;
  meta: string;
  href: string;
  unread: boolean;
  kind: NotificationKind;
  imageSrc?: string;
  onOpen: (href: string) => void;
  onArchive: () => void;
  onToggleRead: () => void;
  dataTestId: string;
};

const KIND_ICON: Record<NotificationKind, ReactElement> = {
  transaction: <MessageSquare className="h-4 w-4" />,
  system: <ShieldCheck className="h-4 w-4" />,
  marketing: <Megaphone className="h-4 w-4" />,
};

const NotificationCard = ({
  title,
  preview,
  meta,
  href,
  unread,
  kind,
  imageSrc,
  onOpen,
  onArchive,
  onToggleRead,
  dataTestId,
}: NotificationCardProps) => {
  const transactionImage = kind === 'transaction' ? imageSrc : undefined;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Close the inline menu when clicking anywhere outside it. No portals →
  // no nested-Radix headache and the parent bell dropdown stays open.
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  const handleAction = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
    setMenuOpen(false);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(href)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(href); } }}
      className="group relative flex w-full items-start gap-3 rounded-[20px] border border-[#e4ddd4] bg-white px-3 py-3 text-left transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:bg-[#fcfaf7] cursor-pointer"
      data-testid={dataTestId}
    >
      {transactionImage ? (
        <div className="relative h-[68px] w-[68px] shrink-0 overflow-hidden rounded-[16px] border border-[#eadfce] bg-[#f6f1ea] shadow-[0_16px_30px_-24px_rgba(23,21,19,0.35)]">
          <img
            src={transactionImage}
            alt={title}
            className="h-full w-full object-cover"
            data-testid={`img-notification-listing-${dataTestId}`}
          />
          {unread && (
            <span className="absolute right-2 top-2 h-2.5 w-2.5 rounded-full bg-primary shadow-[0_0_0_4px_rgba(255,255,255,0.92)]" />
          )}
        </div>
      ) : (
        <div
          className={cn(
            'mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border',
            kind === 'transaction' && 'border-primary/18 bg-[#fffaf4] text-primary',
            kind === 'system' && 'border-[#d6dde6] bg-[#f7f9fb] text-[#49627e]',
            kind === 'marketing' && 'border-[#e3ddd2] bg-[#faf7f2] text-[#7a6b57]'
          )}
          data-testid={`icon-notification-${dataTestId}`}
        >
          {KIND_ICON[kind]}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p
              className="line-clamp-1 text-sm font-semibold text-[#171513]"
              data-testid={`text-notification-title-${dataTestId}`}
            >
              {title}
            </p>
            <p
              className="mt-1 line-clamp-2 text-[13px] leading-5 text-[#5f584e]"
              data-testid={`text-notification-preview-${dataTestId}`}
            >
              {preview}
            </p>
            <p
              className="mt-2 text-[11px] font-medium text-[#8a8378]"
              data-testid={`text-notification-meta-${dataTestId}`}
            >
              {meta}
            </p>
          </div>
          <div className="relative flex shrink-0 items-center gap-1" ref={menuRef}>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setMenuOpen((o) => !o); }}
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded-full text-[#a0998f] transition-opacity hover:bg-[#f4ede3] hover:text-primary group-hover:opacity-100 focus:opacity-100',
                menuOpen ? 'opacity-100' : 'opacity-0'
              )}
              aria-label="Notification options"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              data-testid={`button-notification-menu-${dataTestId}`}
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
            {menuOpen && (
              <div
                role="menu"
                onClick={(e) => e.stopPropagation()}
                className="absolute right-0 top-8 z-50 w-44 overflow-hidden rounded-md border border-[#e4ddd4] bg-white py-1 shadow-lg"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={(e) => handleAction(e, onToggleRead)}
                  className="flex w-full items-center px-3 py-2 text-sm text-[#171513] hover:bg-[#f6f1ea]"
                  data-testid={`menuitem-toggle-read-${dataTestId}`}
                >
                  {unread ? (
                    <><MailOpen className="mr-2 h-4 w-4" /> Mark as read</>
                  ) : (
                    <><MailX className="mr-2 h-4 w-4" /> Mark as unread</>
                  )}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={(e) => handleAction(e, onArchive)}
                  className="flex w-full items-center px-3 py-2 text-sm text-[#171513] hover:bg-[#f6f1ea]"
                  data-testid={`menuitem-archive-${dataTestId}`}
                >
                  <Archive className="mr-2 h-4 w-4" /> Archive
                </button>
              </div>
            )}
            <ArrowUpRight className="mt-0.5 h-4 w-4 text-[#a0998f] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-primary" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationCard;
